import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import PdfViewDrawer from "../../../../../../../../components/PdfViewDrawer";
import RenderWithTooltip from "../../../../../../../../components/RenderWithTooltip";
import { useReduceDisclosure } from "../../../../../../../../hooks/use-reduce-disclosure";
import { useUserArchiveResult } from "../../../../../../../../queries/patient-chart/result/use-archive-result";
import { useGetOrderResults } from "../../../../../../../../queries/patient-chart/result/use-get-all-results";
import {
  formatDateTime,
  NonNullableDate,
} from "../../../../../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  getFormattedId,
  getIdFromObj,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../../utils/utils";
import UpsertResultNew, {
  ABNORMAL_FLAG_OPTIONS_MAPPER,
} from "./UpsertResultNew";
import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";
import { getStoredRealm } from "@/utils/auth-storage";

const OderResult = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams();
  const { mutateAsync: archiveResult } = useUserArchiveResult();
  const archiveResultDisc = useDeleteDisClosure();
  const [editResultDisc, previewResultDisc] = useReduceDisclosure(2);
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const MINIMIZED_RESULT_FIELDS = [
    "patientOrder",
    "resultValue",
    "recorded_date",
    "Action",
  ];

  const { data: orderResults, refetch } = useGetOrderResults({
    patientId: patientId ?? "",
    pagination,
    archive: false,
    gridSorting,
    xTENANTID: getXtenantId(),
  });

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "uuid",
        headerName: "Order ID",
        flex: 0.3,
        width: 100,
        renderCell: ({ row }) => getFormattedId(row?.uuid),
      },
      {
        field: "patientOrder",
        headerName: "Title",
        flex: 0.6,
        width: 200,
        renderCell: ({ value }) => (
          <RenderWithTooltip value={getValueFromObj(value)} />
        ),
      },
      {
        field: "orderFormType",
        headerName: "Mode",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) =>
          value ? replaceUnderScoreAddSpace(value?.toLowerCase()) : "-",
      },
      {
        field: "orderType",
        headerName: "Type",
        flex: 0.6,
        width: 200,
        renderCell: ({ value }) =>
          value ? replaceUnderScoreAddSpace(value?.toLowerCase()) : "-",
      },
      {
        field: "appointmentDate",
        headerName: "Appointment",
        flex: 0.8,
        width: 200,
        renderCell: ({ row }) =>
          row?.appointmentDate ? (
            <Flex gap={1} flexDirection={"column"}>
              {/* <Text isTabletext>{getFormattedId(row?.appointmentId)}</Text> */}
              <Text isTabletext>{formatDateTime(row?.appointmentDate)}</Text>
            </Flex>
          ) : (
            "-"
          ),
      },
      {
        field: "labDescription",
        headerName: "Lab Name",
        flex: 0.6,
        width: 200,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "resultValue",
        headerName: "Result Value",
        flex: 0.5,
        width: 160,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "recorded_date",
        headerName: "Result Date",
        flex: 0.5,
        width: 160,
        renderCell: ({ row }) => NonNullableDate(row?.recordedDate),
      },
      {
        field: "abnormalFlag",
        headerName: "Abnormal Flag",
        flex: 0.5,
        width: 160,
        renderCell: ({ value }) => (
          <RenderWithTooltip
            value={ABNORMAL_FLAG_OPTIONS_MAPPER[value] || "-"}
          />
        ),
      },
      // {
      //   field: "note",
      //   headerName: "Note",
      //   flex: 0.5,
      //   width: 160,
      //   renderCell: ({ value }) => (
      //     <RenderWithTooltip value={value} />
      //   ),
      // },
      {
        field: "Action",
        headerName: "Actions",
        width: 90,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={[
                { value: "view", label: "View Result" },
                { value: "edit", label: "Edit" },
                { value: "delete", label: "Delete" },
              ]}
              onActionClick={(v) => {
                if (v === "view") previewResultDisc.open(row?.labDocument);
                else if (v === "edit") {
                  const initialValues = {
                    ...row,
                    // file: row?.labDocument,
                    // file: undefined,
                    fileName: row?.fileName,
                    abnormalFlag: row?.abnormalFlag
                      ? {
                          value: row?.abnormalFlag,
                          label:
                            ABNORMAL_FLAG_OPTIONS_MAPPER[row?.abnormalFlag],
                        }
                      : undefined,

                    patientOrderId: {
                      value: getIdFromObj(row?.patientOrder),
                      label: getValueFromObj(row?.patientOrder),
                    },
                  };
                  editResultDisc.open(initialValues);
                  return;
                } else archiveResultDisc.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["recorded_date"]
  );

  const filteredColumns = isVisitNotePage
    ? COLUMNS.filter((col) => MINIMIZED_RESULT_FIELDS.includes(col.field))
    : COLUMNS;

  async function archiveHandler<T>(value: boolean, id: T) {
    const res = await archiveResult({
      patientLabResultId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    });
    refetch();
    return res;
  }

  return (
    <Box width={"100%"} height={"100%"}>
      {editResultDisc.opened && (
        <UpsertResultNew disclosureHost={editResultDisc} />
      )}
      <DeleteModal
        w="380px"
        open={archiveResultDisc.opened}
        onClose={archiveResultDisc.close}
        id={archiveResultDisc.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"you want to delete this result?"}
      />

      <PdfViewDrawer
        w="65vw"
        file={previewResultDisc?.data}
        disclosureHost={previewResultDisc}
        title="View Result"
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={orderResults?.data?.content ?? []}
        rowCount={orderResults?.data?.page?.totalElements ?? 10}
        columns={filteredColumns}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        sortModel={gridSorting}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </Box>
  );
};

export default OderResult;
